import { CircularProgress } from "@mui/material";
import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { getAllMunicipalBuildings } from "../api";
import useMunicipality from "../hooks/useMunicipality";
import { motion } from "framer-motion";
import { getImage } from "../utils/utils";
import { BsRecordCircleFill } from "react-icons/bs";
import LevelBadge from "../components/LevelBadge";
import ProgressBar from "../components/ProgressBar";
import RankingBadge from "../components/RankingBadge";
import { getLevelInfo } from "../utils/buildingLevels";

const MunicipalBuildings = ({ isHomePage = false }) => {
    const { municipality } = useMunicipality();

    const { isLoading, isError, error, data } = useQuery(
        ["municipalbuildings", municipality],
        () => getAllMunicipalBuildings({ municipalityId: municipality.MunicipalityId }),
        {
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: false,
            keepPreviousData: true,
        }
    );

    // Sort buildings by points (descending) and add ranking
    const sortedBuildings = useMemo(() => {
        if (!data || !data.buildings) return [];
        
        const getPoints = (buildingId) => {
            const project = data.projects.find(p => p.MunicipalBuildingId === buildingId);
            return project ? project.BalancePoints : 0;
        };
        
        return data.buildings
            .map((building) => ({
                ...building,
                points: getPoints(building.MunicipalBuildingId),
            }))
            .sort((a, b) => b.points - a.points) // Sort by points descending
            .map((building, index) => ({
                ...building,
                rank: index + 1,
            }));
    }, [data]);

    const cardVariants = {
        initial: {
            y: 300,
        },
        exit: {
            y: 0,
        },
        animate: {
            y: 0,
            rotate: 0,
            transition: {
                type: "spring",
                bounce: 0.6,
                duration: 1,
            },
        },
    };

    //error handlers
    if (isLoading)
        return (
            <div className="flex justify-center max-w-2xl px-4 py-8 mx-auto sm:py-10 sm:px-6 lg:max-w-7xl lg:px-8">
                <CircularProgress></CircularProgress>
            </div>
        );

    if (isError) {
        return (
            <div className="flex justify-center max-w-2xl px-4 py-8 mx-auto sm:py-10 sm:px-6 lg:max-w-7xl lg:px-8">
                <div className=" text-center">
                    <p className="text-md font-medium">
                        Ο δήμος δεν έχει δημοτικά κτίρια: {error.message}
                    </p>
                </div>
            </div>
        );
    }

    if (!data || data.buildings.length < 1) return null;

    return (
        <div className="bg-white">
            {isHomePage && data.buildings.length >= 1 ? (
                <div className="flex flex-col space-y-2 md:flex-row items-center justify-between px-4 mx-auto lg:flex lg:px-0 max-w-[1200px] -mb-10 mt-10 ">
                    <div
                        className={`text-white bg-gradient-to-r from-app_green to-app_dark_green font-bold rounded-full text-2xl px-10 py-4 text-center`}
                    >
                        Τα Πράσινα Κτίριά μας
                    </div>
                    <Link
                        to={"buildingcontest"}
                        className="shadow-lg flex justify-center items-center shadow-gray-300 bg-white hover:bg-gradient-to-br drop-shadow-md hover:shadow-md text-app_brown font-semibold rounded-full text-md px-5 text-center h-11 "
                    >
                        <div className="flex items-center">
                            <BsRecordCircleFill></BsRecordCircleFill>
                            <p className="pl-1.5 pb-0.5">{"ΠΕΡΙΣΣΟΤΕΡΑ".toUpperCase()}</p>
                        </div>
                    </Link>
                </div>
            ) : null}
            <div className="max-w-2xl px-4 py-8 mx-auto sm:py-10 sm:px-6 lg:max-w-7xl lg:px-8">
                {!isHomePage && data.buildings.length >= 1 ? (
                    <motion.div
                        className="card-container"
                        variants={cardVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        viewport={{ once: true, amount: 0.8 }}
                    >
                        {data.contest ? (
                            <>
                                <h2 className="pb-4 text-3xl font-bold text-gray-700">
                                    {data.contest.TitleEl || data.contest.TitleEn || "Πράσινα Δημοτικά Κτίρια"}
                                </h2>
                                {data.contest.BodyEl && (
                                    <div 
                                        className="text-lg text-gray-700 pb-8"
                                        dangerouslySetInnerHTML={{ __html: data.contest.BodyEl }}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <h2 className="pb-8 text-3xl font-bold text-gray-700">
                                    Υποστήριξε τα Πράσινα Δημοτικά Κτίρια
                                </h2>
                                <h2 className="text-lg font-semibold text-gray-700">
                                    Μάθε για τα δημοτικά κτίρια που συμμετέχουν στη δράση και 
                                    ενίσχυσέ τα με τους πόντους σου σε κάθε εκπαιδευτική σου δραστηριότητα.
                                </h2>
                            </>
                        )}
                    </motion.div>
                ) : (
                    <></>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-5">
                    {sortedBuildings.map((building) => {
                        const level = getLevelInfo(building.points);
                        
                        return (
                            <motion.div
                                variants={cardVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                viewport={{ once: true, amount: 0.8 }}
                                key={building.MunicipalBuildingId}
                                className="relative overflow-hidden duration-200 ease-out shadow-lg rounded-2xl group hover:-translate-y-2"
                            >
                                <div className="relative w-full rounded-md">
                                    <div className="relative flex h-56 justify-center items-center">
                                        {/* Level Badge - Top Left */}
                                        <div className="absolute top-3 left-3 z-10">
                                            <LevelBadge level={level} />
                                        </div>
                                        
                                        {/* Ranking Badge - Top Right */}
                                        <RankingBadge rank={building.rank} />
                                        
                                        <img
                                            src={
                                                building.Image
                                                    ? getImage(building.Image)
                                                    : "https://via.placeholder.com/400x300.png?text=No+Image"
                                            }
                                            alt={building.Name}
                                            draggable={false}
                                            className="object-cover w-full h-56 rounded-t-2xl"
                                        />
                                    </div>

                                    <div
                                        draggable={false}
                                        className="flex flex-col justify-center w-full min-h-32 content-between px-4 py-3 bg-app_dark_green"
                                    >
                                        <div className="leading-[17px] text-base text-center font-semibold text-white mb-2">
                                            {building.Name}
                                        </div>
                                        
                                        {data.contest && data.contest.IsLive === 1 && data.contest.Status === 3 ? (
                                            <>
                                                <div className="flex items-center justify-center space-x-3 text-[15px] font-semibold text-white mb-2">
                                                    <div>
                                                        Πόντοι: {building.points}
                                                    </div>
                                                    <div className="text-gray-200">
                                                        | Εργαζόμενοι: {building.EmployeeCount || 0}
                                                    </div>
                                                </div>
                                                
                                                {/* Progress Bar */}
                                                <div className="mt-2">
                                                    <ProgressBar points={building.points} level={level} />
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MunicipalBuildings;
